<template>
    <div
        class="user-menu"
        :class="{ 'user-menu_open': isOpen }"
        v-click-outside="close"
        @pointerenter="onPointerEnter"
        @pointerleave="onPointerLeave"
        @keydown.esc="close"
    >
        <button
            type="button"
            class="user-menu__trigger"
            aria-haspopup="menu"
            :aria-expanded="String(isOpen)"
            @pointerdown="lastPointerType = $event.pointerType"
            @click="onTriggerClick"
        >
            <span class="user-menu__avatar">
                <img v-if="showPhoto" :src="avatarUrl" alt="" @error="avatarFailed = true"/>
                <template v-else>{{ initials }}</template>
            </span>
            <span class="user-menu__identity">
                <span class="user-menu__name">{{ displayName }}</span>
                <span class="user-menu__role">{{ roleLabel }}</span>
            </span>
            <svg class="user-menu__icon user-menu__chevron" viewBox="0 0 24 24" aria-hidden="true" v-html="icons.chevronDown"></svg>
        </button>

        <transition name="user-menu">
            <div v-if="isOpen" class="user-menu__panel" role="menu">
                <div class="user-menu__header">
                    <span class="user-menu__avatar user-menu__avatar_large">
                        <img v-if="showPhoto" :src="avatarUrl" alt="" @error="avatarFailed = true"/>
                        <template v-else>{{ initials }}</template>
                    </span>
                    <div class="user-menu__header-text">
                        <div class="user-menu__header-name">{{ displayName }}</div>
                        <div class="user-menu__header-meta">{{ user.email || roleLabel }}</div>
                    </div>
                </div>

                <div class="user-menu__separator"></div>

                <button type="button" role="menuitem" class="user-menu__item" @click="editProfile">
                    <svg class="user-menu__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="icons.user"></svg>
                    <span>{{ $t('Profile') }}</span>
                </button>

                <div class="user-menu__separator"></div>

                <div class="user-menu__section">
                    <div class="user-menu__label">{{ $t('Theme') }}</div>
                    <div class="user-menu__segments user-menu__segments_theme" role="group" :aria-label="$t('Theme')">
                        <button
                            v-for="option in themeOptions"
                            :key="option.value"
                            type="button"
                            class="user-menu__segment"
                            :class="{ 'user-menu__segment_active': theme === option.value }"
                            :aria-pressed="String(theme === option.value)"
                            @click="setTheme(option.value)"
                        >
                            <svg class="user-menu__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="icons[option.icon]"></svg>
                            <span>{{ $t(option.labelKey) }}</span>
                        </button>
                    </div>
                </div>

                <div class="user-menu__section">
                    <div class="user-menu__label">{{ $t('Language') }}</div>
                    <div class="user-menu__segments user-menu__segments_language" role="group" :aria-label="$t('Language')">
                        <button
                            v-for="option in languageOptions"
                            :key="option.value"
                            type="button"
                            class="user-menu__segment"
                            :class="{ 'user-menu__segment_active': language === option.value }"
                            :aria-pressed="String(language === option.value)"
                            @click="setLanguage(option.value)"
                        >
                            <svg class="user-menu__flag" viewBox="0 0 60 40" aria-hidden="true" v-html="option.flag"></svg>
                            <span>{{ option.label }}</span>
                        </button>
                    </div>
                </div>

                <div class="user-menu__separator"></div>

                <button type="button" role="menuitem" class="user-menu__item user-menu__item_danger" @click="logout">
                    <svg class="user-menu__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="icons.logOut"></svg>
                    <span>{{ $t('Logout') }}</span>
                </button>
            </div>
        </transition>
    </div>
</template>

<script>
import { getUserPhoto } from '@/api/api.js'

// Ikony (ścieżki z lucide, jak w service-ui) wstawiane przez v-html do <svg>,
// który ustawia viewBox i obrys — stałe, nie dane od użytkownika.
const ICONS = {
    chevronDown: '<path d="m6 9 6 6 6-6"/>',
    user: '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
    logOut: '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="m16 17 5-5-5-5"/><path d="M21 12H9"/>',
    sun: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/>',
    moon: '<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>',
    monitor: '<rect width="20" height="14" x="2" y="3" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/>'
}

// Flagi jako SVG, bo emoji flag nie renderują się na Windowsie.
const FLAGS = {
    pl: '<rect width="60" height="40" fill="#fff"/><rect y="20" width="60" height="20" fill="#dc143c"/>',
    uk: '<clipPath id="user-menu-uk-clip"><path d="M30,20 h30 v20 z v20 h-30 z h-30 v-20 z v-20 h30 z"/></clipPath>' +
        '<rect width="60" height="40" fill="#012169"/>' +
        '<path d="M0,0 L60,40 M60,0 L0,40" stroke="#fff" stroke-width="8"/>' +
        '<path d="M0,0 L60,40 M60,0 L0,40" clip-path="url(#user-menu-uk-clip)" stroke="#c8102e" stroke-width="5"/>' +
        '<path d="M30,0 v40 M0,20 h60" stroke="#fff" stroke-width="13"/>' +
        '<path d="M30,0 v40 M0,20 h60" stroke="#c8102e" stroke-width="8"/>'
}

const THEME_OPTIONS = [
    { value: 'light', icon: 'sun', labelKey: 'ThemeLight' },
    { value: 'dark', icon: 'moon', labelKey: 'ThemeDark' },
    { value: 'system', icon: 'monitor', labelKey: 'ThemeSystem' }
]

// Nazwy języków zostają w oryginale, jak w dotychczasowym selekcie.
const LANGUAGE_OPTIONS = [
    { value: 'English', label: 'English', flag: FLAGS.uk },
    { value: 'Polish', label: 'Polski', flag: FLAGS.pl }
]

// Menu otwiera się po najechaniu; zwłoka przy zamykaniu pozwala przejechać
// kursorem z przycisku na panel bez gubienia hovera.
const CLOSE_DELAY_MS = 180

export default {
    name: 'UserMenu',
    data() {
        return {
            isOpen: false,
            avatarFailed: false,
            icons: ICONS,
            themeOptions: THEME_OPTIONS,
            languageOptions: LANGUAGE_OPTIONS
        }
    },
    computed: {
        user() {
            return this.$store.getters.user || {}
        },
        displayName() {
            const name = this.user.fullName
            return name && name.trim() !== '' ? name : this.$t('Anonymous')
        },
        roleLabel() {
            const roles = this.user.roles
            return roles && roles[0] ? this.$t(roles[0]) : this.$t('NoRole')
        },
        initials() {
            const name = (this.user.fullName || '').trim()
            if (name === '') return '?'
            return name.split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase()
        },
        // URL liczony raz na zdjęcie, a nie przy każdym renderze — stary
        // getAvatar() tworzył nowy blob URL (i nowe zapytanie) co render.
        avatarUrl() {
            const avatar = this.$store.getters.avatar
            return avatar ? URL.createObjectURL(avatar) : null
        },
        showPhoto() {
            return this.avatarUrl != null && !this.avatarFailed
        },
        theme() {
            return this.$store.getters.theme
        },
        language() {
            return this.$store.getters.language
        }
    },
    watch: {
        avatarUrl(url, previousUrl) {
            if (previousUrl) URL.revokeObjectURL(previousUrl)
            this.avatarFailed = false
        }
    },
    created() {
        // Poza data(), bo to stan pomocniczy, na który szablon nie reaguje.
        this.closeTimer = null
        this.lastPointerType = null
    },
    mounted() {
        if (this.$store.getters.avatar == null) this.loadAvatar()
    },
    beforeDestroy() {
        clearTimeout(this.closeTimer)
        if (this.avatarUrl) URL.revokeObjectURL(this.avatarUrl)
    },
    methods: {
        open() {
            clearTimeout(this.closeTimer)
            this.isOpen = true
        },
        close() {
            clearTimeout(this.closeTimer)
            this.isOpen = false
        },
        // Hover obsługujemy tylko dla myszy. Na dotyku przeglądarka emuluje
        // najechanie przy tapnięciu i menu otwierałoby się i zamykało naraz.
        onPointerEnter(event) {
            if (event.pointerType === 'mouse') this.open()
        },
        onPointerLeave(event) {
            if (event.pointerType !== 'mouse') return
            clearTimeout(this.closeTimer)
            this.closeTimer = setTimeout(() => { this.isOpen = false }, CLOSE_DELAY_MS)
        },
        // Myszą menu jest już otwarte z hovera, więc klik go nie zamyka;
        // dotyk i klawiatura przełączają.
        onTriggerClick() {
            if (this.lastPointerType === 'mouse') {
                this.open()
            } else {
                this.isOpen ? this.close() : this.open()
            }
            this.lastPointerType = null
        },
        editProfile() {
            this.close()
            this.$emit('edit-profile')
        },
        setTheme(value) {
            this.$store.dispatch('change_theme', value)
        },
        // Kultura Syncfusion i część tłumaczeń ustawia się przy starcie,
        // dlatego po zmianie języka przeładowujemy stronę, jak dotychczas.
        setLanguage(value) {
            if (value === this.language) return
            this.$store.dispatch('change_language', value)
            location.reload()
        },
        logout() {
            this.close()
            this.$store.dispatch('logout').then(() => this.$router.push('/'))
        },
        async loadAvatar() {
            if (!this.user.userId) return
            try {
                const response = await getUserPhoto(this.user.userId)
                this.$store.dispatch('update_avatar', response.data)
            } catch (e) {
                // Brak zdjęcia to normalny stan — zostają inicjały.
            }
        }
    }
}
</script>

<style scoped>
.user-menu {
    position: relative;
    display: flex;
    align-items: center;
    align-self: stretch;
}

.user-menu__icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
}

/* --- Przycisk na pasku --- */

.user-menu__trigger {
    display: flex;
    align-items: center;
    gap: 10px;
    max-width: 280px;
    padding: 4px 10px 4px 4px;
    border: none;
    border-radius: 999px;
    background: transparent;
    color: var(--header-fg);
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.user-menu__trigger:hover,
.user-menu_open .user-menu__trigger {
    background-color: var(--header-hover);
}

.user-menu__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    overflow: hidden;
    background-color: rgba(255, 255, 255, 0.18);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.28);
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.02em;
}

.user-menu__avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.user-menu__identity {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.25;
}

.user-menu__name,
.user-menu__role {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.user-menu__name {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--header-fg);
}

.user-menu__role {
    font-size: 11.5px;
    color: var(--header-muted);
}

.user-menu__chevron {
    color: var(--header-muted);
    transition: transform 0.2s ease;
}

.user-menu_open .user-menu__chevron {
    transform: rotate(180deg);
}

/* --- Panel --- */

.user-menu__panel {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 30;
    width: 296px;
    padding: 6px;
    border: 1px solid var(--popover-border);
    border-radius: 14px;
    background-color: var(--popover-bg);
    box-shadow: var(--popover-shadow);
    color: var(--popover-fg);
    transform-origin: top right;
}

/* Mostek nad panelem: kursor przejeżdżający przez odstęp od paska
   nie opuszcza menu, więc hover nie gaśnie po drodze. */
.user-menu__panel::before {
    content: "";
    position: absolute;
    top: -8px;
    left: 0;
    right: 0;
    height: 8px;
}

.user-menu__header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 10px 12px;
}

.user-menu__avatar_large {
    width: 42px;
    height: 42px;
    background-image: linear-gradient(135deg, #0e4759, #2c8ca3);
    box-shadow: none;
    font-size: 15px;
}

.user-menu__header-text {
    min-width: 0;
}

.user-menu__header-name,
.user-menu__header-meta {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.user-menu__header-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--popover-fg);
}

.user-menu__header-meta {
    font-size: 12px;
    color: var(--popover-muted);
}

.user-menu__separator {
    height: 1px;
    margin: 6px -6px;
    background-color: var(--popover-border);
}

.user-menu__item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--popover-fg);
    font-size: 14px;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.12s ease, color 0.12s ease;
}

.user-menu__item .user-menu__icon {
    color: var(--popover-muted);
    transition: color 0.12s ease;
}

.user-menu__item:hover,
.user-menu__item:focus-visible {
    background-color: var(--popover-hover);
}

.user-menu__item_danger:hover,
.user-menu__item_danger:focus-visible,
.user-menu__item_danger:hover .user-menu__icon {
    background-color: var(--danger-hover-bg);
    color: var(--danger);
}

.user-menu__section {
    padding: 6px 4px 8px;
}

.user-menu__label {
    padding: 0 6px 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--popover-muted);
}

/* Przełączniki segmentowe: natywny <select> otwiera listę poza panelem,
   przez co menu z hoverem zamykałoby się w trakcie wyboru. */
.user-menu__segments {
    display: grid;
    gap: 4px;
    padding: 4px;
    border-radius: 10px;
    background-color: var(--segment-bg);
}

.user-menu__segments_theme {
    grid-template-columns: repeat(3, 1fr);
}

.user-menu__segments_language {
    grid-template-columns: repeat(2, 1fr);
}

.user-menu__segment {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-width: 0;
    padding: 7px 6px;
    border: 1px solid transparent;
    border-radius: 7px;
    background: transparent;
    color: var(--popover-muted);
    font-size: 12.5px;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.12s ease, color 0.12s ease;
}

.user-menu__segments_theme .user-menu__segment {
    flex-direction: column;
    gap: 4px;
    padding: 8px 4px;
}

.user-menu__segment:hover {
    color: var(--popover-fg);
}

.user-menu__segment_active,
.user-menu__segment_active:hover {
    border-color: var(--popover-border);
    background-color: var(--segment-active-bg);
    color: var(--segment-active-fg);
    font-weight: 600;
}

.user-menu__segment_active .user-menu__icon {
    color: var(--accent);
}

.user-menu__flag {
    width: 20px;
    height: 14px;
    flex-shrink: 0;
    border-radius: 2px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12);
}

.user-menu-enter-active,
.user-menu-leave-active {
    transition: opacity 0.14s ease, transform 0.14s ease;
}

.user-menu-enter,
.user-menu-leave-to {
    opacity: 0;
    transform: translateY(-4px) scale(0.98);
}

@media only screen and (max-width: 700px) {
    .user-menu__identity,
    .user-menu__chevron {
        display: none;
    }

    .user-menu__trigger {
        padding: 4px;
    }

    .user-menu__panel {
        width: min(296px, calc(100vw - 16px));
    }
}
</style>



// WEBPACK FOOTER //
// src/components/Main/UserMenu.vue